import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, RefreshCw, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { RoleGate } from '@/components/auth/RoleGate';

interface InviteLog {
  id: string;
  created_at: string;
  action: string;
  details: {
    investorEmail?: string;
    companyName?: string;
    shareholderId?: string;
    emailId?: string;
    error?: string;
  };
}

interface InvestorOnboardingTrackerProps {
  companyId: string;
}

export function InvestorOnboardingTracker({ companyId }: InvestorOnboardingTrackerProps) {
  const { toast } = useToast();
  const [logs, setLogs] = useState<InviteLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState<string | null>(null);

  async function loadInvites() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('audit_log')
        .select('id, created_at, action, details')
        .eq('entity_type', 'investor_identity')
        .in('action', ['INVESTOR_INVITE_SENT', 'INVESTOR_INVITE_FAILED'])
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setLogs((data as InviteLog[]) || []);
    } catch (err: any) {
      console.error('Error loading invites:', err);
      toast({
        title: 'Error loading invites',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  async function resendInvite(log: InviteLog) {
    if (!log.details.investorEmail || !log.details.shareholderId || !log.details.companyName) {
      toast({
        title: 'Missing information',
        description: 'Cannot resend invite without complete information',
        variant: 'destructive',
      });
      return;
    }

    setResendingId(log.id);
    try {
      toast({
        title: 'Sending invitation',
        description: `Resending invite to ${log.details.investorEmail}...`,
      });

      const { data, error } = await supabase.functions.invoke('send-investor-invite', {
        body: {
          investorEmail: log.details.investorEmail,
          shareholderId: log.details.shareholderId,
          companyName: log.details.companyName,
        },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to resend invitation');
      }

      toast({
        title: 'Invitation resent',
        description: `Successfully resent invite to ${log.details.investorEmail}`,
      });

      loadInvites();
    } catch (err: any) {
      console.error('Error resending invite:', err);
      toast({
        title: 'Failed to resend',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setResendingId(null);
    }
  }

  useEffect(() => {
    loadInvites();

    // Subscribe to new audit log entries
    const channel = supabase
      .channel('invite_audit_log')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'audit_log',
          filter: `entity_type=eq.investor_identity`,
        },
        () => {
          loadInvites();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <RoleGate allowedRoles={['owner', 'admin']} companyId={companyId}>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Investor Onboarding Tracker</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor invitation status and resend failed invites
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadInvites} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {logs.length > 0 ? (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const isSent = log.action === 'INVESTOR_INVITE_SENT';
                    const isFailed = log.action === 'INVESTOR_INVITE_FAILED';
                    const isResending = resendingId === log.id;

                    return (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          {log.details?.investorEmail || '—'}
                        </TableCell>
                        <TableCell>{log.details?.companyName || '—'}</TableCell>
                        <TableCell>
                          {isSent && (
                            <Badge
                              variant="default"
                              className="bg-success/10 text-success hover:bg-success/20"
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Sent
                            </Badge>
                          )}
                          {isFailed && (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            {new Date(log.created_at).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {isFailed && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendInvite(log)}
                              disabled={isResending}
                            >
                              {isResending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Mail className="h-4 w-4 mr-2" />
                              )}
                              Resend
                            </Button>
                          )}
                          {isSent && log.details?.emailId && (
                            <span className="text-xs text-muted-foreground">
                              ID: {log.details.emailId.slice(0, 8)}...
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground font-medium">No invitations sent yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Send your first invitation from the Investor Mapping tab
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </RoleGate>
  );
}
